import { useCallback } from 'react';

import { datadog } from '@sg/expo-datadog-client';

import { checkIfAwsWafChallengeError } from '../../utils';
import { useAwsWafTokenSource } from '../useAwsWafTokenSource';

import type { CombinedError, Operation } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Logs GraphQL operations that AWS WAF challenged, with the active token source
 * and whether the request carried the token header, to compare token sources in Datadog.
 */
export const useAwsWafChallengeLogger = () => {
  const tokenSource = useAwsWafTokenSource();

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const logAwsWafChallenge = useCallback(
    (params: Params) => {
      const { error, operation, operationName } = params;

      if (!checkIfAwsWafChallengeError(error)) {
        return;
      }

      datadog.logger.error('AWS WAF | Request challenged', {
        tokenSource,
        operationName,
        hasTokenHeader: checkIfRequestHasAwsWafTokenHeader(operation),
      });
    },
    [tokenSource],
  );

  return { logAwsWafChallenge };
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function checkIfRequestHasAwsWafTokenHeader(operation: Operation) {
  const { fetchOptions } = operation.context;
  const requestInit = typeof fetchOptions === 'function' ? fetchOptions() : fetchOptions;

  return new Headers(requestInit?.headers).has(AWS_WAF_TOKEN_HEADER);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const AWS_WAF_TOKEN_HEADER = 'x-aws-waf-token';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  error: CombinedError;
  operation: Operation;
  operationName?: string;
};
